// Regras puras da tela de orçamento (OrcamentoEditor). Sem JSX, para a parte
// arriscada — como o formulário vira os blocos que o PDF e o link público já
// renderizam — ficar isolada e testável.
//
// O orçamento é uma Proposta com `tipo: 'orcamento'`, `modo_design: 'blocos'` e
// uma estrutura fixa de blocos. Tudo que existe hoje para propostas (link
// público, PDF, assinatura, acompanhamento, automações) continua funcionando
// sem saber que o documento nasceu aqui.
import { htmlVazio, ITEM_VAZIO } from '../propostaRapida';

export const VALIDADE_PADRAO = 15;

// Blocos que todo orçamento tem, na ordem em que saem no documento.
const ESTRUTURA = [
  { tipo: 'data', titulo: '', config: { cidade: '' } },
  { tipo: 'dados_cliente', titulo: '', config: {} },
  { tipo: 'itens', titulo: '', config: {} },
  { tipo: 'forma_pagamento', titulo: 'Forma de pagamento', config: {} },
  { tipo: 'validade', titulo: '', config: {} },
  { tipo: 'observacoes', titulo: 'Observações', config: {} }
];

function escaparHtml(texto) {
  return String(texto)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Campos de texto do orçamento são texto simples; o documento guarda HTML.
// Cada linha vira um parágrafo, linha em branco vira parágrafo vazio.
export function textoParaHtml(texto) {
  const linhas = String(texto || '').replace(/\r/g, '').split('\n').map(l => l.trim());
  while (linhas.length && !linhas[linhas.length - 1]) linhas.pop();
  if (!linhas.some(Boolean)) return '';
  return linhas.map(l => `<p>${l ? escaparHtml(l) : '<br>'}</p>`).join('');
}

// Caminho inverso, para abrir um orçamento existente no formulário. Formatação
// (negrito, listas) não sobrevive — por isso o texto só é reconvertido em HTML
// quando o vendedor de fato mexe no campo (ver `dirty` em textosHtml).
export function htmlParaTexto(html) {
  if (htmlVazio(html)) return '';
  return String(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export const tituloAuto = (nome) => {
  const limpo = String(nome || '').trim();
  return limpo ? `Orçamento – ${limpo}` : 'Orçamento';
};

export function calcularItem(item) {
  const bruto = (Number(item.quantidade) || 0) * (Number(item.preco_unitario) || 0);
  return bruto - (bruto * (Number(item.desconto_percent) || 0) / 100);
}

export function calcularTotais(itens, descontoGlobalPercent) {
  const subtotal = (itens || []).reduce((acc, item) => acc + calcularItem(item), 0);
  const desconto = subtotal * ((Number(descontoGlobalPercent) || 0) / 100);
  return { subtotal, desconto, total: subtotal - desconto };
}

export function addDias(base, dias) {
  const d = base ? new Date(base) : new Date();
  d.setDate(d.getDate() + (Number(dias) || 0));
  return d;
}

const normalizarItem = (item) => ({
  descricao: item.descricao || '',
  quantidade: Number(item.quantidade) || 0,
  preco_unitario: Number(item.preco_unitario) || 0,
  desconto_percent: Number(item.desconto_percent) || 0
});

function acharBlocoTexto(blocos, tipo, regex) {
  return blocos.find(b => b.tipo === tipo) || blocos.find(b => regex.test(b.titulo || ''));
}

const campoTexto = (html) => ({ texto: htmlParaTexto(html), html: html || '', dirty: false });

/**
 * Estado do formulário a partir de um orçamento salvo (editar) ou do zero
 * (criar). `template` só entra na criação, para o prazo de validade padrão.
 */
export function estadoInicial({ proposta, template, leadIdParam, chatIdParam } = {}) {
  const blocos = proposta?.blocos || [];
  const blocoPagamento = acharBlocoTexto(blocos, 'forma_pagamento', /pagamento/i);
  const blocoObs = acharBlocoTexto(blocos, 'observacoes', /observa/i);

  const leadPopulado = proposta?.lead_id && typeof proposta.lead_id === 'object' ? proposta.lead_id : null;
  const chatId = proposta?.chat_id && typeof proposta.chat_id === 'object' ? proposta.chat_id._id : proposta?.chat_id;

  return {
    lead: leadPopulado,
    lead_id: leadPopulado?._id || (typeof proposta?.lead_id === 'string' ? proposta.lead_id : null) || leadIdParam || null,
    chat_id: chatId || chatIdParam || null,
    contato: {
      nome: proposta?.contato?.nome || '',
      email: proposta?.contato?.email || '',
      telefone: proposta?.contato?.telefone || ''
    },
    itens: proposta?.itens?.length ? proposta.itens.map(normalizarItem) : [{ ...ITEM_VAZIO }],
    desconto_global_percent: proposta?.desconto_global_percent || 0,
    validade_dias: proposta?.validade_dias || template?.validade_dias_default || VALIDADE_PADRAO,
    pagamento: campoTexto(blocoPagamento?.conteudo_html),
    observacoes: campoTexto(blocoObs?.conteudo_html),
    mensagens_ia: proposta?.mensagens_ia || [],
    template_id: proposta ? null : (template?._id || null)
  };
}

// HTML que vai para o documento: campo mexido é reconvertido do texto; campo
// intocado devolve o HTML original (preserva formatação vinda da IA ou do editor).
export function textosHtml(estado) {
  return {
    pagamentoHtml: estado.pagamento.dirty ? textoParaHtml(estado.pagamento.texto) : (estado.pagamento.html || ''),
    observacoesHtml: estado.observacoes.dirty ? textoParaHtml(estado.observacoes.texto) : (estado.observacoes.html || '')
  };
}

/**
 * Blocos a gravar. Num orçamento novo é a estrutura fixa. Num existente, os
 * blocos são preservados (título, ordem, config) e só o texto de pagamento e
 * observações é atualizado; blocos dinâmicos que faltarem são acrescentados.
 */
export function blocosParaSalvar(blocosAtuais, { pagamentoHtml, observacoesHtml }) {
  const conteudoPor = { forma_pagamento: pagamentoHtml, observacoes: observacoesHtml };

  if (!blocosAtuais?.length) {
    return ESTRUTURA.map((b, i) => ({ ...b, conteudo_html: conteudoPor[b.tipo] || '', ordem: i }));
  }

  const ordenados = [...blocosAtuais].sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
  const usados = new Set();
  const blocos = ordenados.map((bloco) => {
    const alvo = ['forma_pagamento', 'observacoes'].find(tipo =>
      !usados.has(tipo) && (bloco.tipo === tipo || (tipo === 'forma_pagamento' ? /pagamento/i : /observa/i).test(bloco.titulo || ''))
    );
    if (!alvo) return bloco;
    usados.add(alvo);
    return { ...bloco, conteudo_html: conteudoPor[alvo] || '' };
  });

  let maiorOrdem = blocos.reduce((max, b) => Math.max(max, b.ordem || 0), -1);
  ESTRUTURA.forEach((b) => {
    const existe = blocos.some(x => x.tipo === b.tipo || (b.tipo === 'itens' && x.tipo === 'tabela_precos'));
    if (existe || (b.tipo === 'forma_pagamento' && usados.has('forma_pagamento')) || (b.tipo === 'observacoes' && usados.has('observacoes'))) return;
    maiorOrdem += 1;
    blocos.push({ ...b, conteudo_html: conteudoPor[b.tipo] || '', ordem: maiorOrdem });
  });

  return blocos;
}

// Itens que valem a pena gravar: com descrição ou com preço.
export const itensPreenchidos = (itens) =>
  (itens || []).filter(i => (i.descricao || '').trim() || Number(i.preco_unitario) > 0).map(normalizarItem);

/**
 * Corpo do POST/PUT. Não envia `layout` na criação: o controller herda o do
 * modelo (logo, cor, dados da empresa, rodapé). Totais, número e status são
 * calculados no servidor. Título é derivado do cliente e só é regenerado se
 * ainda for o automático — um título editado no editor completo é respeitado.
 */
export function montarPayloadOrcamento(estado, { proposta } = {}) {
  const { pagamentoHtml, observacoesHtml } = textosHtml(estado);
  const tituloAtual = proposta?.titulo;
  const titulo = !tituloAtual || tituloAtual === tituloAuto(proposta?.contato?.nome)
    ? tituloAuto(estado.contato.nome)
    : tituloAtual;

  return {
    tipo: 'orcamento',
    titulo,
    lead_id: estado.lead_id || undefined,
    chat_id: estado.chat_id || undefined,
    contato: {
      nome: (estado.contato.nome || '').trim(),
      email: (estado.contato.email || '').trim(),
      telefone: (estado.contato.telefone || '').trim()
    },
    itens: itensPreenchidos(estado.itens),
    desconto_global_percent: Number(estado.desconto_global_percent) || 0,
    validade_dias: Number(estado.validade_dias) || VALIDADE_PADRAO,
    modo_design: 'blocos',
    blocos: blocosParaSalvar(proposta?.blocos, { pagamentoHtml, observacoesHtml }),
    template_id: proposta ? undefined : (estado.template_id || undefined),
    mensagens_ia: estado.mensagens_ia?.length ? estado.mensagens_ia : undefined
  };
}

/**
 * Objeto no formato de Proposta para a miniatura renderizar com o mesmo
 * componente do documento (DocumentCanvas em modo leitura) — assim a prévia é o
 * que o cliente recebe, não uma segunda interpretação.
 */
export function propostaParaPreview(estado, { proposta, template } = {}) {
  const { pagamentoHtml, observacoesHtml } = textosHtml(estado);
  return {
    ...(proposta || {}),
    titulo: proposta?.titulo && proposta.titulo !== tituloAuto(proposta?.contato?.nome)
      ? proposta.titulo
      : tituloAuto(estado.contato.nome),
    contato: estado.contato,
    itens: estado.itens,
    desconto_global_percent: estado.desconto_global_percent,
    validade_dias: estado.validade_dias,
    layout: proposta?.layout || template?.layout || { tema_cor: '#7C3AED', empresa: {} },
    blocos: blocosParaSalvar(proposta?.blocos, { pagamentoHtml, observacoesHtml }),
    created_at: proposta?.created_at
  };
}

/**
 * Resposta do agente (POST /propostas/gerar-com-ia) aplicada ao formulário.
 * Só a parte estruturada: itens e os dois textos. O HTML da IA é guardado
 * intacto (dirty=false) para não perder formatação ao salvar.
 */
export function aplicarRespostaIaOrcamento(resposta, estado) {
  const { itens, observacoes, forma_pagamento: formaPagamento } = resposta || {};
  const proximo = { ...estado };
  const mudou = [];

  if (itens?.length) {
    proximo.itens = itens.map(normalizarItem);
    mudou.push(itens.length === 1 ? '1 item' : `${itens.length} itens`);
  }
  if (formaPagamento && !htmlVazio(formaPagamento)) {
    proximo.pagamento = campoTexto(formaPagamento);
    mudou.push('a forma de pagamento');
  }
  if (observacoes && !htmlVazio(observacoes)) {
    proximo.observacoes = campoTexto(observacoes);
    mudou.push('as observações');
  }

  return { estado: proximo, mudou };
}
